import { useNavigate, useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Ticket } from '../../types/ticket';
import TicketStatusBadge from './TicketStatusBadge';
import TicketPriorityBadge from './TicketPriorityBadge';
import ConfirmationModal from './ConfirmationModal';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Priority, Status } from '../../types';
import { useAuthContext } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

interface TicketTableProps {
  tickets: Ticket[];
  currentPage: number;
  onTicketClick: (ticketId: number) => void;
  onBulkStatusChange?: (ticketIds: number[], newStatus: Status) => void;
  isBulkStatusChangeDisabled?: boolean;
}

export default function TicketTable({ 
  tickets, 
  currentPage, 
  onTicketClick,
  onBulkStatusChange,
  isBulkStatusChangeDisabled = false
}: TicketTableProps) {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { theme } = useTheme();
  
  // Multi-select state
  const [selectedTickets, setSelectedTickets] = useState(new Set<number>(JSON.parse(localStorage.getItem('myWorkspaceSelectedTickets') || '[]')));
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  
  // Confirmation modal state
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    status: Status;
    statusLabel: string;
  } | null>(null);

  // Handle select all checkbox
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedTickets(new Set(tickets.map(ticket => ticket.id)));
      localStorage.setItem('myWorkspaceSelectedTickets', JSON.stringify(Array.from(new Set(tickets.map(ticket => ticket.id)))));
    } else {
      setSelectedTickets(new Set());
      localStorage.setItem('myWorkspaceSelectedTickets', JSON.stringify([]));
    }
  };

  // Handle individual ticket selection
  const handleTicketSelect = (ticketId: number, checked: boolean) => {
    const newSelected = new Set(selectedTickets);
    if (checked) {
      newSelected.add(ticketId);
    } else {
      newSelected.delete(ticketId);
    }
    setSelectedTickets(newSelected);
    localStorage.setItem('myWorkspaceSelectedTickets', JSON.stringify(Array.from(newSelected)));
  };

  // Handle bulk status change
  const handleBulkStatusChange = (newStatus: Status) => {
    const statusLabel = statusOptions.find(s => s.value === newStatus)?.label || newStatus;
    setPendingStatusChange({ status: newStatus, statusLabel });
    setIsConfirmModalOpen(true);
    setShowStatusDropdown(false);
  };

  // Confirm status change
  const confirmStatusChange = () => {
    if (onBulkStatusChange && selectedTickets.size > 0 && pendingStatusChange) {
      onBulkStatusChange(Array.from(selectedTickets), pendingStatusChange.status);
      setSelectedTickets(new Set());
      localStorage.setItem('myWorkspaceSelectedTickets', JSON.stringify([]));
      setIsConfirmModalOpen(false);
      setPendingStatusChange(null);
    }
  };

  // Cancel status change
  const cancelStatusChange = () => {
    setIsConfirmModalOpen(false);
    setPendingStatusChange(null);
  };

  // Calculate selection state
  const isAllSelected = tickets.length > 0 && selectedTickets.size === tickets.length;
  const isPartialSelected = selectedTickets.size > 0 && selectedTickets.size < tickets.length;

  // Status Options Colors for bulk actions select
  const statusOptions = [
    { value: 'new' as Status, label: t('tickets.status.new'), color: 'bg-green-500' },
    { value: 'processing' as Status, label: t('tickets.status.processing'), color: 'bg-yellow-500' },
    { value: 'resolved' as Status, label: t('tickets.status.resolved'), color: 'bg-blue-500' },
    { value: 'closed' as Status, label: t('tickets.status.closed'), color: 'bg-gray-500' },
  ];

  // Status Options Colors for Modal Confirmation
  const statusColorsText = [
    { value: 'new' as Status, label: t('tickets.status.new'), color: 'text-green-400' },
    { value: 'processing' as Status, label: t('tickets.status.processing'), color: 'text-yellow-500' },
    { value: 'resolved' as Status, label: t('tickets.status.resolved'), color: 'text-blue-500' },
    { value: 'closed' as Status, label: t('tickets.status.closed'), color: 'text-gray-500' },
  ];

  return (
    <div className="space-y-4">
      {/* Bulk Actions Bar */}
      {selectedTickets.size > 0 && (
        <div className={`flex items-center justify-between p-4 rounded-lg border ${
          theme === 'dark' 
            ? 'bg-gray-800 border-gray-700' 
            : 'bg-blue-50 border-blue-200'
        }`}>
          <div className={`text-sm font-medium ${
            theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
          }`}>
            {selectedTickets.size} {t('tickets.selected')}
          </div>
          <div className="flex items-center space-x-3">
            {/* Status Change Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                className={`flex items-center space-x-2 px-4 py-2 text-sm rounded-md border ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span>{t('tickets.bulkActions.changeStatus')}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m19 9-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {showStatusDropdown && (
                <div className={`absolute right-0 mt-2 w-48 rounded-md shadow-lg z-10 ${
                  theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                } border`}>
                  <div className="py-1">
                    {statusOptions.map((status) => (
                      <button
                        key={status.value}
                        onClick={() => handleBulkStatusChange(status.value)}
                        className={`flex items-center w-full px-4 py-2 text-sm text-left ${
                          theme === 'dark'
                            ? 'text-gray-300 hover:bg-gray-700'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <div className={`w-3 h-3 rounded-full mr-3 ${status.color}`}></div>
                        {status.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => {
                setSelectedTickets(new Set());
                setShowStatusDropdown(false);
              }}
              className={`px-3 py-2 text-sm rounded border ${
                theme === 'dark'
                  ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className={`overflow-x-auto rounded-lg border ${
        theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
      }`}>
        <table className={`min-w-full divide-y ${
          theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'
        }`}>
          <thead className={theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}>
            <tr className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
              {/* Select All Checkbox */}
              {isBulkStatusChangeDisabled && (
                <th className={`px-6 py-3 text-left ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                }`}>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = isPartialSelected;
                    }}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${
                      theme === 'dark' ? 'bg-gray-700 border-gray-600' : ''
                    }`}
                  />
                </th>
              )}
              
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>{t('tickets.columns.id')}</th>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>{t('tickets.columns.title')}</th>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>{t('tickets.columns.customer')}</th>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>{t('tickets.columns.status')}</th>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>{t('tickets.columns.priority')}</th>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>{t('tickets.columns.type')}</th>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>{t('tickets.columns.category')}</th>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>{t('tickets.columns.source')}</th>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>{t('tickets.columns.assignedTo')}</th>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>{t('tickets.columns.createdAt')}</th>
            </tr>
          </thead>
          <tbody className={`divide-y ${
            theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'
          }`}>
            {tickets.map((ticket) => {
              const isAssignedToMe = ticket.assigned_to_id === user?.id;
              const isSelected = selectedTickets.has(ticket.id);

              return (
                <tr
                  key={ticket.id}
                  className={`transition-colors ${
                    isSelected
                      ? theme === 'dark'
                        ? 'bg-blue-900/40'
                        : 'bg-blue-100'
                      : theme === 'dark'
                        ? `${isAssignedToMe 
                            ? 'bg-blue-900/20 hover:bg-blue-900/30' 
                            : 'hover:bg-gray-700'}`
                        : `${isAssignedToMe 
                            ? 'bg-blue-50 hover:bg-blue-100' 
                            : 'hover:bg-gray-50'}`
                  }`}
                >
                  {/* Checkbox */}
                  {isBulkStatusChangeDisabled && (
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => handleTicketSelect(ticket.id, e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                    onBlur={() => setTimeout(() => setShowStatusDropdown(false), 150)}
                      className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${
                        theme === 'dark' ? 'bg-gray-700 border-gray-600' : ''
                      }`}
                    />
                  </td>
                  )}
                  {/* Existing columns - wrapped in clickable div */}
                  <td 
                    className={`px-6 py-4 whitespace-nowrap text-sm cursor-pointer ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}
                    onClick={() => onTicketClick(ticket.id)}
                  >
                    #{ticket.id}
                  </td>
                  
                  <td 
                    className="px-6 py-4 whitespace-nowrap cursor-pointer"
                    onClick={() => onTicketClick(ticket.id)}
                  >
                    <div>
                      <div className={`text-sm font-medium ${
                        theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                      }`}>{ticket.title}</div>
                      <div className={`text-sm truncate max-w-xs ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>{ticket.description}</div>
                    </div>
                  </td>
                  
                  <td 
                    className="px-6 py-4 whitespace-nowrap cursor-pointer"
                    onClick={() => onTicketClick(ticket.id)}
                  >
                    <div className="flex items-center">
                      <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center font-semibold ${
                        theme === 'dark' 
                          ? 'bg-blue-900/30 text-blue-400' 
                          : 'bg-blue-100 text-blue-600'
                      }`}>
                        {ticket['customer.full_name']?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div className="ml-3">
                        <div className={`text-sm font-medium ${
                          theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                        }`}>{ticket['customer.full_name']}</div>
                        {ticket['customer.phone'] && (
                          <div className={`text-sm ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                          }`}>{ticket['customer.phone']}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  
                  <td 
                    className="px-6 py-4 whitespace-nowrap cursor-pointer"
                    onClick={() => onTicketClick(ticket.id)}
                  >
                    <TicketStatusBadge status={ticket.status as Status} />
                  </td>
                  
                  <td 
                    className="px-6 py-4 whitespace-nowrap cursor-pointer"
                    onClick={() => onTicketClick(ticket.id)}
                  >
                    <TicketPriorityBadge priority={ticket.priority as Priority} />
                  </td>
                  
                  <td 
                    className="px-6 py-4 whitespace-nowrap text-sm cursor-pointer"
                    onClick={() => onTicketClick(ticket.id)}
                  >
                    {ticket.type_name && (
                      <span className={`px-2 py-1 text-xs rounded-md ${
                        theme === 'dark'
                          ? 'bg-gray-700 text-gray-300'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {ticket.type_name}
                      </span>
                    )}
                  </td>
                  
                  <td 
                    className="px-6 py-4 whitespace-nowrap text-sm cursor-pointer"
                    onClick={() => onTicketClick(ticket.id)}
                  >
                    {ticket.category_name && (
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        theme === 'dark'
                          ? 'bg-indigo-900/30 text-indigo-400'
                          : 'bg-indigo-50 text-indigo-700'
                      }`}>
                        {ticket.category_name}
                      </span>
                    )}
                  </td>
                  
                  <td 
                    className="px-6 py-4 whitespace-nowrap text-sm cursor-pointer"
                    onClick={() => onTicketClick(ticket.id)}
                  >
                    {ticket.source_name && (
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        theme === 'dark'
                          ? 'bg-purple-900/30 text-purple-400'
                          : 'bg-purple-50 text-purple-700'
                      }`}>
                        {ticket.source_name}
                      </span>
                    )}
                  </td>
                  
                  <td 
                    className={`px-6 py-4 whitespace-nowrap text-sm cursor-pointer ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}
                    onClick={() => onTicketClick(ticket.id)}
                  >
                    {ticket['assignedTo.fullName'] || t('tickets.unassigned')}
                  </td>
                  
                  <td 
                    className={`px-6 py-4 whitespace-nowrap text-sm cursor-pointer ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}
                    onClick={() => onTicketClick(ticket.id)}
                  >
                    {new Date(ticket.created_at).toLocaleString()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Confirmation Modal */}
      {pendingStatusChange && (
        <ConfirmationModal
          isOpen={isConfirmModalOpen}
          onClose={cancelStatusChange}
          onConfirm={confirmStatusChange}
          title={t('tickets.bulkActions.confirmTitle')}
          message={
            <Trans
              i18nKey="tickets.bulkActions.confirmMessage"
              values={{ count: selectedTickets.size, status: pendingStatusChange.statusLabel }}
              components={{ strong: <span className={`font-bold ${statusColorsText.find(s => s.value === pendingStatusChange.status)?.color}`} /> }}
            />
          }
          confirmText={t('tickets.bulkActions.confirm')}
          confirmButtonClass="bg-blue-600 hover:bg-blue-700"
          icon={
            <ExclamationTriangleIcon className="w-6 h-6 text-yellow-500" />
          }
        />
      )}
    </div>
  );
}
